import { useEffect, useId, useState } from 'react';
import { finiteOr } from '@/shared/lib/utils/finite-or';
import { useGraphRendererApi } from '../model/use-api';
import type { GraphViewport, GraphViewportController } from '../model/types';

export type GraphBackgroundProps = {
  controller?: GraphViewportController | null;
  gridSpacing?: number;
};

const DEFAULT_VIEWPORT: GraphViewport = { x: 0, y: 0, zoom: 1 };

const normalizeViewport = (viewport: GraphViewport): GraphViewport => ({
  x: finiteOr(viewport.x, DEFAULT_VIEWPORT.x),
  y: finiteOr(viewport.y, DEFAULT_VIEWPORT.y),
  zoom:
    Number.isFinite(viewport.zoom) && viewport.zoom > 0
      ? viewport.zoom
      : DEFAULT_VIEWPORT.zoom,
});

export const Background = ({
  controller,
  gridSpacing = 100,
}: GraphBackgroundProps) => {
  const getApi = useGraphRendererApi();
  const [viewport, setViewport] = useState(DEFAULT_VIEWPORT);
  const patternId = `graph-dot-pattern-${useId().replaceAll(':', '')}`;

  useEffect(() => {
    const viewportController = controller ?? getApi();
    if (!viewportController) return;

    setViewport(normalizeViewport(viewportController.getViewport()));
    return viewportController.subscribeViewport((nextViewport) =>
      setViewport(normalizeViewport(nextViewport)),
    );
  }, [controller, getApi]);

  const spacing = Math.max(gridSpacing * viewport.zoom, 1);

  return (
    <svg
      width='100%'
      height='100%'
      className='pointer-events-none absolute inset-0'
      style={{
        inset: 0,
        pointerEvents: 'none',
        position: 'absolute',
        zIndex: 0,
      }}
      data-testid='graph-background'
      aria-hidden='true'
    >
      <defs>
        <pattern
          id={patternId}
          patternUnits='userSpaceOnUse'
          x={viewport.x}
          y={viewport.y}
          width={spacing}
          height={spacing}
          data-testid='graph-background-pattern'
        >
          <circle
            cx={spacing / 2}
            cy={spacing / 2}
            r={2}
            fill='currentColor'
            opacity={0.1}
          />
        </pattern>
      </defs>
      <rect width='100%' height='100%' fill={`url(#${patternId})`} />
    </svg>
  );
};